import { EventEmitter, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Operation } from '@factor_ec/utils';
import { MessageService } from '@factor_ec/ui';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '@/core/api.service';
import { SessionService } from '@/core/session.service';
import { Group } from '../models/group';
import { GroupComponent } from '../components/group/group.component';

@Injectable({
  providedIn: 'root'
})
export class GroupService {
  // Dependency injection
  private readonly apiService = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly messageService = inject(MessageService);
  private readonly sessionService = inject(SessionService);

  // Events
  public readonly change: EventEmitter<Operation> = new EventEmitter();

  /**
   * @description Abre el diálogo para crear o editar un grupo
   * @param {Group} [group] - Grupo existente para editar (opcional)
   * @returns {Promise<Operation>} Promesa con la operación resultante
   */
  public async open(group?: Group): Promise<Operation> {
    return new Promise<Operation>(resolve => {
      const dialogRef = this.dialog.open(GroupComponent, {
        data: { group },
        panelClass: 'ft-dialog',
        width: '400px'
      });
      dialogRef.componentInstance.afterSubmit.subscribe(
        (operation: Operation) => {
          this.change.emit(operation);
          resolve(operation);
        }
      );
    });
  }

  /**
   * @description Elimina un grupo tras confirmación del usuario
   * @param {Group} group - Grupo a eliminar
   * @returns {Promise<void>}
   */
  public async removeGroup(group: Group): Promise<void> {
    const value = await firstValueFrom(
      this.messageService.show(
        $localize`Do you really want to delete group "${group.description} (#${group.sequence})"?`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'error',
            class: 'text-danger ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Cancel`,
              value: 0,
              type: 'stroked',
              class: 'flex-grow-1'
            },
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      )
    );
    if (value === 1) {
      const lenderId = this.sessionService.settings()?.lenderId;
      await this.apiService.call({
        microServiceName: 'WSMPGroupManagement',
        resourcePath: `/group/delete-group/{lenderId}/${group.sequence}`,
        method: 'DELETE',
        useToken: true,
        pathParams: {
          lenderId: {
            value: lenderId,
            encrypted: true
          }
        }
      });
      this.change.emit({
        method: 'delete',
        entity: group
      });
      this.messageService.show($localize`Group deleted successfully.`, {
        class: 'ft-message--success',
        icon: 'check--circle'
      });
    }
  }
}
